import { useState, useEffect, useCallback } from "react";
import { clearAllWritingDrafts } from "@/lib/writing-draft-storage";
import { useLocation, useNavigate } from "react-router-dom";
import { ADMIN_NAV, REGULAR_ADMIN_NAV, CENTRE_MANAGER_NAV, TUTOR_NAV, STATE_PARTNER_NAV, GROWTH_PARTNER_NAV, FRANCHISEE_PROSPECT_NAV, PARENT_NAV, STUDENT_NAV } from "./navRouteConfig";
import type { NavRouteItem, AdminNavConfig, SectionRouteConfig } from "./navRouteConfig";
import { ICON_MAP } from "./RailIcons";
import { ItemIcon, ChevronIcon } from "./ItemIcons";
import lurneaxLogo from "@/assets/lurneax-logo.svg";
import { useRole } from "@/contexts/RoleContext";
import { useProjectName } from "@/hooks/use-project-name";
import { supabase } from "@/integrations/supabase/client";
import { useIsMobile } from "@/hooks/use-mobile";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { usePreloadOnIntent, preloadPath } from "@/lib/routePreload";
import "./navShell.css";

// Section display metadata
export const SECTION_META_MAP: Record<string, { label: string; railLabel: string }> = {
  network: { label: "Network", railLabel: "Network" },
  assessments: { label: "Assessments", railLabel: "Quiz" },
  learning: { label: "Learning", railLabel: "Learning" },
  parents_students: { label: "Parents & Students", railLabel: "Parents" },
  comms: { label: "Communications", railLabel: "Comms" },
  system: { label: "System", railLabel: "System" },
  admin: { label: "Admin", railLabel: "Admin" },
};

/** Get all items from a section, including those inside groups */
function getAllSectionItems(section: SectionRouteConfig): NavRouteItem[] {
  const items = [...section.items];
  if (section.groups) {
    for (const group of section.groups) {
      items.push(...group.items);
    }
  }
  return items;
}

/**
 * Returns true if `itemPath` (which may include a query string) matches the
 * current `pathname` + `search`. Path is matched by prefix; every query param
 * in `itemPath` must be present with the same value in `search`.
 */
function pathMatches(itemPath: string, pathname: string, search: string): boolean {
  const [base, query] = itemPath.split("?");
  if (!pathname.startsWith(base)) return false;
  if (!query) return true;
  const current = new URLSearchParams(search);
  const target = new URLSearchParams(query);
  for (const [k, v] of target.entries()) {
    if (current.get(k) !== v) return false;
  }
  return true;
}

/**
 * Finds which section and item are active based on current route path + search.
 * Longer item paths (including their query string) win, so query-scoped items
 * (e.g. ?scope=exam) beat broader parent paths on the same pathname.
 */
function findActiveFromPath(
  config: AdminNavConfig,
  pathname: string,
  search: string
): { section: string; itemLabel: string } {
  let bestMatch = { section: config.rail[0], itemLabel: "", matchLen: 0 };

  for (const sectionKey of config.rail) {
    const section = config.sections[sectionKey];
    if (!section) continue;
    const allItems = getAllSectionItems(section);
    for (const item of allItems) {
      if (item.path && pathMatches(item.path, pathname, search) && item.path.length > bestMatch.matchLen) {
        bestMatch = { section: sectionKey, itemLabel: item.label, matchLen: item.path.length };
      }
      if (item.children) {
        for (const child of item.children) {
          if (child.path && pathMatches(child.path, pathname, search) && child.path.length > bestMatch.matchLen) {
            bestMatch = { section: sectionKey, itemLabel: child.label, matchLen: child.path.length };
          }
        }
      }
    }
  }

  return { section: bestMatch.section, itemLabel: bestMatch.itemLabel };
}

export function NavShellLayout({ children }: { children: React.ReactNode }) {
  const { userRole, allRoles } = useRole();
  const { data: projectName } = useProjectName();
  const location = useLocation();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Franchisee prospects always get the restricted prospect nav, even if
  // they happen to carry another role alongside it.
  const isProspect = userRole === "franchisee_prospect" || (allRoles ?? []).includes("franchisee_prospect");

  const config =
    isProspect ? FRANCHISEE_PROSPECT_NAV :
    userRole === "master_admin" ? ADMIN_NAV :
    userRole === "center_manager" ? CENTRE_MANAGER_NAV :
    userRole === "tutor" ? TUTOR_NAV :
    userRole === "state_partner" ? STATE_PARTNER_NAV :
    userRole === "growth_partner" ? GROWTH_PARTNER_NAV :
    userRole === "parent" ? PARENT_NAV :
    userRole === "student" ? STUDENT_NAV :
    REGULAR_ADMIN_NAV;
  const { section: detectedSection, itemLabel: detectedItem } = findActiveFromPath(config, location.pathname, location.search);

  const [activeSection, setActiveSection] = useState(detectedSection);
  const [panelVisible, setPanelVisible] = useState(true);
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());

  // Sync active section when route changes
  useEffect(() => {
    const { section } = findActiveFromPath(config, location.pathname, location.search);
    setActiveSection(section);
  }, [location.pathname, location.search, config]);

  // Close drawer on route change (mobile)
  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  const handleSectionClick = useCallback(
    (section: string) => {
      if (section === activeSection) return;
      setPanelVisible(false);
      setTimeout(() => {
        setActiveSection(section);
        setExpandedItems(new Set());
        setPanelVisible(true);
        // Navigate to first item in section
        const firstItem = config.sections[section]?.items[0];
        if (firstItem?.path) navigate(firstItem.path);
      }, 150);
    },
    [activeSection, config, navigate]
  );

  const handleItemClick = useCallback(
    (item: NavRouteItem) => {
      if (item.href) {
        window.open(item.href, "_blank", "noopener,noreferrer");
        return;
      }
      if (item.path) navigate(item.path);
    },
    [navigate]
  );

  const toggleExpand = useCallback((label: string) => {
    setExpandedItems((prev) => {
      const next = new Set(prev);
      if (next.has(label)) next.delete(label);
      else next.add(label);
      return next;
    });
  }, []);

  const handleSignOut = async () => {
    try {
      await clearAllWritingDrafts(); // explicit user sign-out only
      await supabase.auth.signOut();
    } catch {
      console.warn("SignOut API failed, force-clearing local session");
    }
    for (const key of Object.keys(localStorage)) {
      if (key.startsWith("sb-") || key.startsWith("supabase")) {
        localStorage.removeItem(key);
      }
    }
    localStorage.removeItem("lurneax_active_role");
    navigate("/login");
  };

  const sectionMeta = SECTION_META_MAP[activeSection];
  const sectionLabel = sectionMeta?.label ?? activeSection;

  // Auto-expand parent items if a child route is active
  useEffect(() => {
    const section = config.sections[activeSection];
    if (!section) return;
    const newExpanded = new Set<string>();
    const allItems = getAllSectionItems(section);
    for (const item of allItems) {
      if (item.children) {
        for (const child of item.children) {
          if (child.path && pathMatches(child.path, location.pathname, location.search)) {
            newExpanded.add(item.label);
          }
        }
      }
    }
    if (newExpanded.size > 0) {
      setExpandedItems((prev) => new Set([...prev, ...newExpanded]));
    }
  }, [location.pathname, location.search, activeSection, config]);

  /* ── Shared panel content renderer ── */
  const renderPanelContent = () => (
    <>
      <div className="panel-header">
        {projectName && <p className="panel-project-name">{projectName}</p>}
        <p className="panel-section-label">{sectionLabel}</p>
        {userRole && (
          <span className="panel-role-badge">
            {userRole.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
          </span>
        )}
      </div>
      <div className={`panel-body ${panelVisible ? "panel-body--visible" : "panel-body--hidden"}`}>
        {config.sections[activeSection]?.items.map((item) => (
          <PanelRouteItem
            key={item.label}
            item={item}
            currentPath={location.pathname}
            currentSearch={location.search}
            onNavigate={handleItemClick}
            expandedItems={expandedItems}
            onToggleExpand={toggleExpand}
          />
        ))}
        {config.sections[activeSection]?.groups?.map((group) => (
          <div key={group.groupLabel} className="panel-group">
            <p className="panel-group-label">{group.groupLabel}</p>
            {group.items.map((item) => (
              <PanelRouteItem
                key={item.label}
                item={item}
                currentPath={location.pathname}
                currentSearch={location.search}
                onNavigate={handleItemClick}
                expandedItems={expandedItems}
                onToggleExpand={toggleExpand}
              />
            ))}
          </div>
        ))}
      </div>
    </>
  );

  return (
    <div className="nav-shell-wrapper">
      {/* ── Mobile header ── */}
      <div className="mobile-nav-header">
        <button className="mobile-hamburger" onClick={() => setDrawerOpen(true)} aria-label="Open menu">
          <svg width={22} height={22} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round">
            <line x1="3" y1="6" x2="21" y2="6" />
            <line x1="3" y1="12" x2="21" y2="12" />
            <line x1="3" y1="18" x2="21" y2="18" />
          </svg>
        </button>
        <div className="mobile-header-logo" onClick={() => navigate("/learning/dashboard")}>
          <img src={lurneaxLogo} alt="Lurneax" />
        </div>
        <span className="mobile-header-title">{sectionLabel}</span>
      </div>

      {/* ── Mobile drawer ── */}
      {isMobile && (
        <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
          <SheetContent side="left" className="p-0 w-[300px] flex flex-col" style={{ background: 'var(--ns-bg)', color: 'var(--ns-text-primary)' }}>
            {/* Section pills */}
            <div className="mobile-drawer-rail">
              {config.rail.map((section) => {
                const Icon = ICON_MAP[section];
                const meta = SECTION_META_MAP[section];
                const isActive = activeSection === section;
                const firstPath = config.sections[section]?.items[0]?.path;
                const warm = () => preloadPath(firstPath);
                return (
                  <button
                    key={section}
                    className={`rail-item ${isActive ? "rail-item--active" : ""}`}
                    onClick={() => handleSectionClick(section)}
                    onPointerEnter={warm}
                    onFocus={warm}
                    onTouchStart={warm}
                  >
                    {Icon && <Icon />}
                    <span className="rail-label">{meta?.railLabel ?? section}</span>
                  </button>
                );
              })}
            </div>

            {/* Panel items */}
            <div className="mobile-drawer-panel">
              {renderPanelContent()}
            </div>

            {/* Footer: Help + Sign Out */}
            <div className="mobile-drawer-footer">
              {!isProspect && (
                <button
                  className="rail-item"
                  onClick={() => window.open("https://nuansis.com/helpdesk", "_blank", "noopener,noreferrer")}
                >
                  {ICON_MAP.help && <ICON_MAP.help />}
                  <span className="rail-label">Help</span>
                </button>
              )}
              <button className="rail-item rail-signout" onClick={handleSignOut}>
                <svg width={16} height={16} viewBox="0 0 24 24" fill="none" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" stroke="currentColor">
                  <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                  <polyline points="16 17 21 12 16 7" />
                  <line x1="21" y1="12" x2="9" y2="12" />
                </svg>
                <span className="rail-label">Sign Out</span>
              </button>
            </div>
          </SheetContent>
        </Sheet>
      )}

      <div className="nav-grid">
        {/* Col 1 — Icon rail */}
        <aside className="icon-rail">
          <div className="rail-top">
            <button className="rail-logo" onClick={() => navigate("/learning/dashboard")}>
              <img src={lurneaxLogo} alt="Lurneax" className="rail-logo-img" />
            </button>

            {config.rail.map((section) => {
              const Icon = ICON_MAP[section];
              const meta = SECTION_META_MAP[section];
              const isActive = activeSection === section;
              const firstPath = config.sections[section]?.items[0]?.path;
              const warm = () => preloadPath(firstPath);
              return (
                <button
                  key={section}
                  className={`rail-item ${isActive ? "rail-item--active" : ""}`}
                  onClick={() => handleSectionClick(section)}
                  onPointerEnter={warm}
                  onFocus={warm}
                  onTouchStart={warm}
                >
                  {Icon && <Icon />}
                  <span className="rail-label">{meta?.railLabel ?? section}</span>
                </button>
              );
            })}
          </div>

          <div className="rail-bottom">
            {!isProspect && (
              <button
                className="rail-item"
                onClick={() => window.open("https://nuansis.com/helpdesk", "_blank", "noopener,noreferrer")}
              >
                {ICON_MAP.help && <ICON_MAP.help />}
                <span className="rail-label">Help</span>
              </button>
            )}
            <button className="rail-item rail-signout" onClick={handleSignOut}>
              <svg
                width={16}
                height={16}
                viewBox="0 0 24 24"
                fill="none"
                strokeWidth={1.5}
                strokeLinecap="round"
                strokeLinejoin="round"
                stroke="currentColor"
              >
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                <polyline points="16 17 21 12 16 7" />
                <line x1="21" y1="12" x2="9" y2="12" />
              </svg>
              <span className="rail-label">Sign Out</span>
            </button>
          </div>
        </aside>

        {/* Col 2 — Fly-out panel */}
        <nav className="flyout-panel">
          {renderPanelContent()}
        </nav>

        {/* Col 3 — Main content area */}
        <div className="main-area">
          <main className="navshell-main-content">
            {children}
          </main>
        </div>
      </div>
    </div>
  );
}

/* ── Panel item with routing ── */
function PanelRouteItem({
  item,
  currentPath,
  currentSearch,
  onNavigate,
  expandedItems,
  onToggleExpand,
  isChild = false,
}: {
  item: NavRouteItem;
  currentPath: string;
  currentSearch: string;
  onNavigate: (item: NavRouteItem) => void;
  expandedItems: Set<string>;
  onToggleExpand: (label: string) => void;
  isChild?: boolean;
}) {
  const isActive = item.path ? pathMatches(item.path, currentPath, currentSearch) : false;
  // Don't mark parent as active if a child is active
  const childActive = item.children?.some((c) => c.path && pathMatches(c.path, currentPath, currentSearch));
  const isItemActive = isActive && !childActive;
  const hasChildren = !!item.children?.length;
  const isExpanded = expandedItems.has(item.label);

  const preloadProps = usePreloadOnIntent(item.path);
  return (
    <>
      <div className={`panel-item-row ${isChild ? "panel-item-row--child" : ""}`}>
        <button
          className={`panel-item ${isItemActive ? "panel-item--active" : ""} ${isChild ? "panel-item--child" : ""}`}
          onClick={() => onNavigate(item)}
          {...preloadProps}
        >
          {isItemActive && <span className="active-dot" />}
          <span className="panel-item-icon">
            <ItemIcon name={item.icon} />
          </span>
          <span className="panel-item-label">{item.label}</span>
        </button>
        {hasChildren && (
          <button
            className="panel-expand-btn"
            onClick={(e) => {
              e.stopPropagation();
              onToggleExpand(item.label);
            }}
            aria-label={isExpanded ? "Collapse" : "Expand"}
          >
            <ChevronIcon expanded={isExpanded} />
          </button>
        )}
      </div>
      {hasChildren && isExpanded && (
        <div className="panel-children">
          {item.children!.map((child) => (
            <PanelRouteItem
              key={child.label}
              item={child}
              currentPath={currentPath}
              currentSearch={currentSearch}
              onNavigate={onNavigate}
              expandedItems={expandedItems}
              onToggleExpand={onToggleExpand}
              isChild
            />
          ))}
        </div>
      )}
    </>
  );
}
